import { FC } from 'react'

import styled from 'styled-components'

import { Modal } from 'src/Components/Modal'
import { Timeline } from 'src/Components/Timeline'
import { Badge } from 'src/Components/Badge'
import { Loader } from 'src/Components/Loader'
import {
  useGetApprovalHistoryQuery,
  ApprovalStatusEnum,
} from 'src/Redux/api/documentsApi'
import { colors } from 'src/Helpers/constants'

const Stack = styled.div<{ $align?: string; $py?: string; $gap?: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => $align || 'flex-start'};
  padding-top: ${({ $py }) => ($py === 'xl' ? '32px' : '0')};
  padding-bottom: ${({ $py }) => ($py === 'xl' ? '32px' : '0')};
  gap: ${({ $gap }) =>
    $gap === 'xs' ? '4px' : $gap === 'sm' ? '8px' : '16px'};
  margin-top: ${({ $py }) => ($py === 'md' ? '16px' : '0')};
`

const ErrorText = styled.span`
  color: ${colors.red};
  text-align: center;
  padding: 32px 0;
  display: block;
`

const EmptyText = styled.span`
  color: ${colors.thirdGray};
  opacity: 0.7;
  text-align: center;
  padding: 32px 0;
  display: block;
`

const InfoText = styled.span<{
  $size?: string
  $fw?: number
  $dimmed?: boolean
}>`
  font-size: ${({ $size }) => ($size === 'sm' ? '13px' : '14px')};
  font-weight: ${({ $fw }) => $fw || 400};
  color: ${colors.thirdGray};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.7 : 1)};
`

const Group = styled.div<{ $gap?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ $gap }) => ($gap === 'sm' ? '8px' : '12px')};
`

const HistoryItem = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
`

interface DocumentApprovalHistoryModalProps {
  opened: boolean
  onClose: () => void
  documentId: string
  contractNumber?: string
}

const getStatusColor = (status: ApprovalStatusEnum): string => {
  switch (status) {
    case ApprovalStatusEnum.APPROVED:
      return 'green'
    case ApprovalStatusEnum.REJECTED:
      return 'red'
    case ApprovalStatusEnum.IN_PROCESS:
      return 'blue'
    case ApprovalStatusEnum.SKIPPED:
      return 'orange'
    default:
      return 'gray'
  }
}

const getStatusLabel = (status: ApprovalStatusEnum): string => {
  switch (status) {
    case ApprovalStatusEnum.APPROVED:
      return 'Согласовано'
    case ApprovalStatusEnum.REJECTED:
      return 'Отклонено'
    case ApprovalStatusEnum.IN_PROCESS:
      return 'На согласовании'
    case ApprovalStatusEnum.PENDING:
      return 'Ожидает'
    case ApprovalStatusEnum.SKIPPED:
      return 'Пропущено'
    default:
      return 'Неизвестно'
  }
}

export const DocumentApprovalHistoryModal: FC<
  DocumentApprovalHistoryModalProps
> = ({ opened, onClose, documentId, contractNumber }) => {
  const { data, isLoading, error } = useGetApprovalHistoryQuery(documentId, {
    skip: !opened,
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`История согласования${contractNumber ? ` №${contractNumber}` : ''}`}
      size='lg'
    >
      {isLoading && (
        <Stack $align='center' $py='xl'>
          <Loader size='lg' />
        </Stack>
      )}

      {error && (
        <ErrorText>Не удалось загрузить историю согласования</ErrorText>
      )}

      {data && data.history.length === 0 && (
        <EmptyText>История согласования пуста</EmptyText>
      )}

      {data && data.history.length > 0 && (
        <Timeline active={-1} bulletSize={32} lineWidth={2}>
          {data.history.map((item) => (
            <Timeline.Item
              key={item.id}
              color={getStatusColor(item.status)}
              title={
                <Group $gap='sm'>
                  <InfoText $fw={600}>{item.user.name}</InfoText>
                  <Badge size='sm' color={getStatusColor(item.status)}>
                    {getStatusLabel(item.status)}
                  </Badge>
                  <Badge size='sm' variant='light'>
                    Этап {item.order}
                  </Badge>
                </Group>
              }
            >
              <HistoryItem>
                <Stack $gap='xs'>
                  <InfoText $size='sm' $dimmed>
                    {item.user.email}
                  </InfoText>

                  {item.startDate && (
                    <InfoText $size='sm'>
                      <strong>Начало:</strong>{' '}
                      {new Date(item.startDate).toLocaleString('ru-RU')}
                    </InfoText>
                  )}

                  {item.deadline && (
                    <InfoText $size='sm'>
                      <strong>Дедлайн:</strong>{' '}
                      {new Date(item.deadline).toLocaleDateString('ru-RU')}
                    </InfoText>
                  )}

                  {item.completedAt && (
                    <InfoText $size='sm'>
                      <strong>Завершено:</strong>{' '}
                      {new Date(item.completedAt).toLocaleString('ru-RU')}
                    </InfoText>
                  )}

                  {item.comment && (
                    <InfoText $size='sm'>
                      <strong>Комментарий:</strong> {item.comment}
                    </InfoText>
                  )}
                </Stack>
              </HistoryItem>
            </Timeline.Item>
          ))}
        </Timeline>
      )}
    </Modal>
  )
}
